import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDailyQuotaStore, DAILY_LIMITS } from '@/store/daily-quota.store';
import { useAuthStore } from '@/store';
import './layout.css';

const STORAGE_KEY = 'sidebar-collapsed';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
}

/* Pipeline / Kanban icon */
const PipelineIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
    />
  </svg>
);

/* Bar chart icon */
const AnalyticsIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

/* Documents / files icon */
const DocumentsIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
    />
  </svg>
);

/* ATS / shield-check icon */
const ATSIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

/* User / profile icon */
const ProfileIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

/* Gift / referral icon */
const ReferralIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
    />
  </svg>
);

/* Collapse toggle icons */
const CollapseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
    />
  </svg>
);

const ExpandIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 5l7 7-7 7M5 5l7 7-7 7"
    />
  </svg>
);

const navItems: NavItem[] = [
  {
    name: 'Pipeline',
    path: '/app/pipeline',
    icon: <PipelineIcon />,
  },
  {
    name: 'ATS Analysis',
    path: '/app/dashboard',
    icon: <ATSIcon />,
  },
  {
    name: 'Analytics',
    path: '/app/analytics',
    icon: <AnalyticsIcon />,
  },
  {
    name: 'Documents',
    path: '/app/documents',
    icon: <DocumentsIcon />,
  },
  {
    name: 'Profile',
    path: '/app/profile',
    icon: <ProfileIcon />,
  },
  {
    name: 'Referral',
    path: '/app/referral',
    icon: <ReferralIcon />,
  },
];

export function Sidebar() {
  const location = useLocation();
  const { getRemainingGenerations, checkAndResetDate } = useDailyQuotaStore();
  const user = useAuthStore((s) => s.user);
  const isPro = user?.tier === 'pro';
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Ensure quota date is current
  useEffect(() => {
    checkAndResetDate();
  }, [checkAndResetDate]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // localStorage may be unavailable
    }
  }, [collapsed]);

  const isActive = (path: string) => {
    // Exact match or starts with path (for nested routes like /app/pipeline/:jobId)
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const remaining = getRemainingGenerations();
  const total = DAILY_LIMITS.cvGenerations;
  const percentage = (remaining / total) * 100;

  // Color based on percentage
  const getQuotaColor = () => {
    if (percentage > 50) return 'text-success';
    if (percentage > 20) return 'text-warning';
    return 'text-error';
  };

  return (
    <aside
      className={`
        bg-surface border-r border-border
        sticky top-14 h-[calc(100vh-3.5rem)]
        flex flex-col justify-between
        transition-all duration-300 ease-in-out overflow-y-auto
        ${collapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Navigation items */}
      <nav className="p-2 space-y-1 mt-2">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? item.name : undefined}
              className={`
                flex items-center gap-3 px-3 py-3 rounded-lg transition-colors relative
                ${collapsed ? 'justify-center' : ''}
                ${
                  active
                    ? 'bg-primary-soft text-primary font-medium border-l-[3px] border-l-primary'
                    : 'text-text-primary hover:bg-primary-soft border-l-[3px] border-l-transparent'
                }
              `}
            >
              {item.icon}
              {!collapsed && <span className="text-sm">{item.name}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section: extension promo + quota + collapse toggle */}
      <div className="p-2 space-y-2 mb-2">
        {/* Chrome Extension promo */}
        <a
          href="https://chromewebstore.google.com/detail/ats-cv-checker/acfhjcapfkflfjdgcmhfnjhmjhagllgn?utm_source=webapp&utm_medium=sidebar"
          target="_blank"
          rel="noopener noreferrer"
          title="Get Chrome Extension for the full experience"
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg
            bg-gradient-to-r from-primary/10 to-primary/5
            border border-primary/20
            text-primary hover:border-primary/40 hover:from-primary/15
            transition-all group
            ${collapsed ? 'justify-center' : ''}
          `}
        >
          <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0112 6.545h10.691A12 12 0 0012 0z" fill="#EA4335"/>
            <path d="M1.931 5.47A11.943 11.943 0 000 12c0 4.25 2.214 7.98 5.554 10.104l3.96-6.861a5.442 5.442 0 01-2.06-5.318L1.931 5.47z" fill="#34A853"/>
            <path d="M12 17.455a5.456 5.456 0 01-3.768-1.51l-3.96 6.86C6.239 24.163 8.998 24 12 24c2.933 0 5.735-.794 8.138-2.189l-3.96-6.86A5.432 5.432 0 0112 17.456z" fill="#FBBC05"/>
            <path d="M22.372 7.09H12a5.455 5.455 0 013.786 9.402l3.96 6.86A11.945 11.945 0 0024 12c0-1.79-.391-3.489-1.094-5.013l-.534.103z" fill="#4285F4"/>
            <circle cx="12" cy="12" r="3.273" fill="currentColor"/>
          </svg>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold leading-tight">Get Extension</p>
              <p className="text-[10px] leading-tight opacity-70 mt-0.5">Real-time ATS scores on job pages</p>
            </div>
          )}
          {!collapsed && (
            <svg className="w-3 h-3 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          )}
        </a>

        {/* Daily quota indicator */}
        <div
          className={`
            px-3 py-2 rounded-lg bg-primary-soft
            ${collapsed ? 'text-center' : ''}
          `}
        >
          {collapsed ? (
            <span
              className={`text-xs font-medium ${isPro ? 'text-primary' : getQuotaColor()}`}
              title={isPro ? 'Pro Plan — Unlimited' : `${remaining} CV generations left today`}
            >
              {isPro ? '∞' : remaining}
            </span>
          ) : isPro ? (
            <div className="sidebar-quota--pro">
              <div className="sidebar-quota--pro-top">
                <span className="sidebar-quota__plan-label">Pro Plan</span>
                <span className="sidebar-quota__unlimited">Unlimited</span>
              </div>
              <Link to="/app/profile" className="sidebar-quota__manage-billing">
                Manage billing
              </Link>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-text-primary">Daily Quota</p>
                <span className={`text-xs font-semibold ${getQuotaColor()}`}>
                  {remaining}/{total}
                </span>
              </div>
              <div className="w-full bg-surface rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    percentage > 50
                      ? 'bg-success'
                      : percentage > 20
                        ? 'bg-warning'
                        : 'bg-error'
                  }`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <p className="text-xs text-text-secondary mt-1">
                {remaining} CV generation{remaining === 1 ? '' : 's'} left today
              </p>
              <Link to="/pricing" className="sidebar-quota__upgrade-link">
                Upgrade to Pro
              </Link>
            </div>
          )}
        </div>

        {/* Collapse toggle button */}
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className={`
            flex items-center gap-2 w-full px-3 py-2 rounded-lg
            text-text-secondary hover:bg-primary-soft hover:text-text-primary
            transition-colors
            ${collapsed ? 'justify-center' : ''}
          `}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ExpandIcon /> : <CollapseIcon />}
          {!collapsed && <span className="text-sm">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
